import {
  LoadScript,
  GoogleMap,
  OverlayView,
  Marker,
  Circle,
} from "@react-google-maps/api";
import { useStyles, calculateRegion, MapProps } from "./map-shared";
import React, { useState, useRef } from "react";
import { Text, IconButton, useTheme, FAB } from "react-native-paper";
import { View, Linking, StyleSheet } from "react-native";
import { useMapStyle } from "../theme";

// Get Google Maps API key from environment variables (injected at build time by webpack)
const GOOGLE_MAPS_API_KEY = process.env.GOOGLE_MAPS_API_KEY || '';

const MARKER_LAT_OFFSET = 0.0001;

function StaticMap({ lat, lon, files, onRefresh }: MapProps) {
  const [selected, setSelected] = useState();
  const mapRef = useRef<google.maps.Map | null>(null);

  const styles = useStyles();
  const mapStyle = useMapStyle();
  const theme = useTheme();

  const mapOptions = {
    panControl: false,
    zoomControl: false,
    draggable: true,
    mapTypeControl: false,
    scrollwheel: true,
    streetViewControl: false,
    fullscreenControl: false,
    styles: mapStyle,
  };

  const region = calculateRegion(lat, lon);
  const mapStyles = {
    width: "100%",
    height: "100vh",
  };

  const getPixelPositionOffset = (width, height) => ({
    x: 0,
    y: -height,
  });

  const handleZoomIn = () => {
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom() || 15;
      mapRef.current.setZoom(currentZoom + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom() || 15;
      mapRef.current.setZoom(currentZoom - 1);
    }
  };

  const handleRecenter = () => {
    if (mapRef.current) {
      mapRef.current.panTo(region);
      mapRef.current.setZoom(15);
    }
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <View style={{ flex: 1, position: 'relative' }}>
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={mapStyles}
          zoom={15}
          options={mapOptions}
          center={region}
          onLoad={(map) => { mapRef.current = map; }}
        >
          {/* User's current location marker */}
          <Marker
            position={region}
            icon={{
              path: "M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z",
              fillColor: theme.colors.primary,
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
              scale: 1,
            }}
            zIndex={1000}
          />

          {/* 2km radius circle around user location */}
          <Circle
            center={region}
            radius={2000} // 2km in meters
            options={{
              strokeColor: theme.colors.primary,
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: theme.colors.primary,
              fillOpacity: 0.15,
            }}
          />

          {files
            ? files
                .filter((file) => {
                  // Calculate distance from user location (approximate)
                  const R = 6371e3; // Earth radius in meters
                  const φ1 = (lat * Math.PI) / 180;
                  const φ2 = (file.lat * Math.PI) / 180;
                  const Δφ = ((file.lat - lat) * Math.PI) / 180;
                  const Δλ = ((file.lon - lon) * Math.PI) / 180;

                  const a =
                    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
                    Math.cos(φ1) *
                      Math.cos(φ2) *
                      Math.sin(Δλ / 2) *
                      Math.sin(Δλ / 2);
                  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                  const distance = R * c; // Distance in meters

                  // Only show files within 2km radius
                  return distance <= 2000;
                })
                .map((file) => (
                  <Marker
                    key={file.url}
                    position={{ lat: file.lat, lng: file.lon }}
                    icon={{
                      url: require("../assets/icons/marker-icon.png"),
                    }}
                    onClick={() => setSelected(file)}
                  />
                ))
            : null}

          {selected && (
            <OverlayView
              position={{
                lat: selected.lat + MARKER_LAT_OFFSET,
                lng: selected.lon,
              }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={getPixelPositionOffset}
            >
              <View style={styles.customCallout}>
                <View style={styles.calloutContainer}>
                  {/* Header with title */}
                  <View style={styles.calloutHeader}>
                    <Text 
                      style={styles.calloutTitle}
                      numberOfLines={2}
                      ellipsizeMode="tail"
                    >
                      {selected.name}
                    </Text>
                  </View>
                  
                  {/* Body with description */}
                  <View style={styles.calloutBody}>
                    <Text 
                      style={styles.calloutDescription}
                      numberOfLines={3}
                      ellipsizeMode="tail"
                    >
                      {selected.description || "No description"}
                    </Text>
                  </View>
                  
                  {/* Footer with action buttons */}
                  <View style={styles.calloutFooter}>
                    <IconButton
                      icon="open-in-new"
                      size={20}
                      iconColor={theme.colors.primary}
                      style={styles.calloutButton}
                      onPress={() => {
                        Linking.openURL(selected.url);
                      }}
                    />
                    <IconButton
                      icon="close"
                      size={20}
                      iconColor={theme.colors.onSurfaceVariant}
                      style={styles.calloutButton}
                      onPress={() => {
                        setSelected(null);
                      }}
                    />
                  </View>
                </View>
              </View>
            </OverlayView>
          )}
        </GoogleMap>
      </LoadScript>
      
      {/* Custom control buttons */}
      <View style={controlStyles.container}>
        <FAB
          icon="plus"
          size="small"
          style={[controlStyles.button, { backgroundColor: theme.colors.surface }]}
          color={theme.colors.primary}
          onPress={handleZoomIn}
        />
        <FAB
          icon="minus"
          size="small"
          style={[controlStyles.button, { backgroundColor: theme.colors.surface, marginTop: 8 }]}
          color={theme.colors.primary}
          onPress={handleZoomOut}
        />
        <FAB
          icon="crosshairs-gps"
          size="small"
          style={[controlStyles.button, { backgroundColor: theme.colors.surface, marginTop: 16 }]}
          color={theme.colors.primary}
          onPress={handleRecenter}
        />
        <FAB
          icon="refresh"
          size="small"
          style={[controlStyles.button, { backgroundColor: theme.colors.surface, marginTop: 8 }]}
          color={theme.colors.primary}
          onPress={handleRefresh}
        />
      </View>
    </View>
  );
}

const controlStyles = StyleSheet.create({
  container: {
    position: 'absolute',
    right: 16,
    top: 16,
    flexDirection: 'column',
    zIndex: 1000,
  },
  button: {
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
});

export default StaticMap;
